import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { calendar as calApi } from '@/utils/api';
import { Zap, Check, Clock } from 'lucide-react';
import { toast } from 'sonner';

function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function BookMeeting() {
  const { operatorId } = useParams();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ attendee_name: '', attendee_email: '', title: 'Discovery Call', notes: '' });
  const [booked, setBooked] = useState(null);

  const loadSlots = (d) => {
    setLoadingSlots(true);
    setSelectedSlot(null);
    calApi.publicSlots(operatorId, d)
      .then(r => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  };
  useEffect(() => { loadSlots(date); }, [date, operatorId]);

  const book = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return toast.error('Select a time slot');
    try {
      const r = await calApi.publicBook(operatorId, {
        start: selectedSlot.start,
        end: selectedSlot.end,
        attendee_name: form.attendee_name,
        attendee_email: form.attendee_email,
        title: form.title,
        notes: form.notes,
      });
      setBooked(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Booking failed'); }
  };

  if (booked) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#FAF8F4', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
        <div className="max-w-md text-center p-8">
          <Check size={48} style={{ color: '#006080', margin: '0 auto 16px' }} />
          <h1 className="text-2xl font-black mb-2" style={{ color: '#1A2428' }}>Meeting Confirmed</h1>
          <p className="text-sm mb-2" style={{ color: '#5C6B72' }}>{booked.title}</p>
          <p className="text-sm font-semibold" style={{ color: '#1A2428' }}>{new Date(booked.start).toLocaleString()}</p>
          {booked.meet_link && (
            <a href={booked.meet_link} target="_blank" rel="noreferrer" className="inline-block mt-4 px-5 py-2 rounded-btn text-white text-sm" style={{ background: '#006080' }}>
              Join Google Meet
            </a>
          )}
          <p className="text-xs mt-4" style={{ color: '#5C6B72' }}>Confirmation sent to {booked.attendee_email}</p>
        </div>
      </div>
    );
  }

  // Date picker: next 14 days
  const dates = Array.from({ length: 14 }).map((_, i) => addDays(new Date(), i));

  return (
    <div className="min-h-screen px-4 py-10" style={{ background: '#FAF8F4', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#006080' }}>
            <Zap size={14} color="white" strokeWidth={2.5} />
          </div>
          <span className="font-bold text-base" style={{ color: '#1A2428' }}>Book a meeting</span>
        </div>

        <div className="rounded-card p-6" style={{ background: 'white', border: '1px solid #E8E3D9' }}>
          <h2 className="text-sm font-bold mb-2" style={{ color: '#1A2428' }}>1. Pick a date</h2>
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
            {dates.map(d => {
              const ds = d.toISOString().slice(0, 10);
              return (
                <button key={ds} onClick={() => setDate(ds)} className="flex-shrink-0 w-14 py-2 rounded-btn text-center transition-colors" style={{ background: date === ds ? '#006080' : '#FAF8F4', color: date === ds ? 'white' : '#1A2428', border: '1px solid #E8E3D9' }} data-testid={`date-${ds}`}>
                  <p className="text-xs uppercase tracking-wide">{d.toLocaleDateString([], { weekday: 'short' })}</p>
                  <p className="text-sm font-bold">{d.getDate()}</p>
                </button>
              );
            })}
          </div>

          <h2 className="text-sm font-bold mb-2" style={{ color: '#1A2428' }}>2. Pick a time</h2>
          {loadingSlots ? (
            <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#006080', borderTopColor: 'transparent' }} />
          ) : slots.length === 0 ? (
            <p className="text-sm py-4" style={{ color: '#5C6B72' }}>No availability on this day.</p>
          ) : (
            <div className="grid grid-cols-4 gap-2 mb-4">
              {slots.map(s => (
                <button key={s.start} onClick={() => setSelectedSlot(s)} className="py-2 rounded-btn text-sm flex items-center justify-center gap-1" style={{ background: selectedSlot?.start === s.start ? '#006080' : '#FAF8F4', color: selectedSlot?.start === s.start ? 'white' : '#1A2428', border: '1px solid #E8E3D9' }} data-testid={`slot-${s.start}`}>
                  <Clock size={11} /> {new Date(s.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </button>
              ))}
            </div>
          )}

          {selectedSlot && (
            <form onSubmit={book} className="mt-4 pt-4 space-y-3" style={{ borderTop: '1px solid #E8E3D9' }}>
              <h2 className="text-sm font-bold" style={{ color: '#1A2428' }}>3. Your details</h2>
              <div className="grid grid-cols-2 gap-3">
                <input className="input-agency text-sm" placeholder="Your name" required value={form.attendee_name} onChange={e => setForm({ ...form, attendee_name: e.target.value })} data-testid="booking-name" />
                <input className="input-agency text-sm" placeholder="Email" type="email" required value={form.attendee_email} onChange={e => setForm({ ...form, attendee_email: e.target.value })} data-testid="booking-email" />
              </div>
              <input className="input-agency text-sm" placeholder="What should we discuss?" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
              <textarea className="input-agency text-sm resize-none" rows={2} placeholder="Any context / notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              <button type="submit" className="w-full py-2.5 rounded-btn text-white font-semibold text-sm" style={{ background: '#006080' }} data-testid="booking-confirm">
                Confirm Booking · {new Date(selectedSlot.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
